import { PILLS, type PillSpec } from '@/spaces/chat/chatEmptyStateData';

export type StrategyGoal =
	| 'grow_awareness'
	| 'generate_leads'
	| 'nurture_customers';

export type SlotId =
	| 'igContent'
	| 'autoReplies'
	| 'paidAds'
	| 'findTrends'
	| 'emailMarketing'
	| 'marketResearch';

type SlotDef = {
	label: string;
	lead: string;
	short: string;
	blockedWhenNoBudget?: boolean;
};

export const SLOT_DEFS: Record<SlotId, SlotDef> = {
	igContent: {
		label: 'Instagram Content',
		lead: 'keeping your feed alive with posts and carousels that sound like you — I draft, you approve, we post',
		short: 'a steady Instagram feed in your voice',
	},
	autoReplies: {
		label: 'Automate Engagement',
		lead: 'turning every DM and comment into a real conversation — I reply in your voice, around the clock',
		short: 'DMs and comments handled around the clock',
	},
	paidAds: {
		label: 'Paid Ads',
		lead: 'targeted paid ads behind your best content — we start small, scale what works',
		short: 'targeted paid ads that amplify your wins',
		blockedWhenNoBudget: true,
	},
	findTrends: {
		label: 'Spot Trends',
		lead: "catching what's heating up in your space — so we ride the right trends before everyone else does",
		short: 'riding the right trends before they peak',
	},
	emailMarketing: {
		label: 'Email Marketing',
		lead: 'emails that keep your customers close — launches, win-backs, the occasional check-in, all in your voice',
		short: 'emails that keep customers coming back',
	},
	marketResearch: {
		label: 'Market Research',
		lead: "eyes on similar businesses and the conversations your customers are having — so we always know what's working and what's missing",
		short: 'eyes on similar businesses and customer conversations',
	},
};

export const SLOT_TO_PILL_LABEL: Record<SlotId, string> = {
	igContent: 'Instagram Content',
	autoReplies: 'Automate Engagement',
	paidAds: 'Paid Ads',
	findTrends: 'Spot Trends',
	emailMarketing: 'Email Marketing',
	marketResearch: 'Market Research',
};

export const pillForSlot = (slot: SlotId): PillSpec => {
	const pill = PILLS.find((p) => p.label === SLOT_TO_PILL_LABEL[slot]);
	if (!pill) {
		throw new Error(`No pill found for slot ${slot}`);
	}
	return pill;
};

const ALL_SLOTS: SlotId[] = [
	'igContent',
	'autoReplies',
	'paidAds',
	'findTrends',
	'emailMarketing',
	'marketResearch',
];

function isBlocked(slot: SlotId, budget: number): boolean {
	return !!SLOT_DEFS[slot].blockedWhenNoBudget && budget <= 0;
}

function scoreAllSlots(
	goal: StrategyGoal | null,
	budget: number,
): Record<SlotId, number> {
	const scores: Record<SlotId, number> = {
		igContent: 0,
		autoReplies: 0,
		paidAds: 0,
		findTrends: 0,
		emailMarketing: 0,
		marketResearch: 0,
	};

	if (goal === 'grow_awareness') {
		scores.igContent += 5;
		scores.findTrends += 4;
		scores.marketResearch += 3;
		scores.paidAds += 3;
		scores.autoReplies += 1.5;
		scores.emailMarketing += 0.5;
	} else if (goal === 'generate_leads') {
		scores.autoReplies += 5;
		scores.igContent += 4;
		scores.paidAds += 4;
		scores.marketResearch += 3;
		scores.findTrends += 1;
		scores.emailMarketing += 2;
	} else if (goal === 'nurture_customers') {
		scores.emailMarketing += 5;
		scores.autoReplies += 4;
		scores.igContent += 3;
		scores.marketResearch += 2;
		scores.findTrends += 1;
		scores.paidAds += 0.5;
	}

	if (budget >= 500) scores.paidAds += 1.5;
	else if (budget >= 100) scores.paidAds += 0.5;

	scores.igContent += 0.3;
	scores.marketResearch += 0.2;

	return scores;
}

function computeAllRanked(goal: StrategyGoal | null, budget: number): SlotId[] {
	const scores = scoreAllSlots(goal, budget);
	return ALL_SLOTS.slice()
		.sort((a, b) => scores[b] - scores[a])
		.filter((s) => !isBlocked(s, budget));
}

function capitalize(s: string): string {
	if (!s) return s;
	return s.charAt(0).toUpperCase() + s.slice(1);
}

const GOAL_PHRASE: Record<StrategyGoal, string> = {
	grow_awareness: 'going hard on awareness',
	generate_leads: 'going for new customers',
	nurture_customers: 'keeping your existing customers close',
};

function skipFrameFor(slot: SlotId, budget: number): string {
	const hasBudget = budget > 0;
	switch (slot) {
		case 'igContent':
			return hasBudget
				? "posting on Instagram — we'll let the paid moves carry the visibility this quarter"
				: "a big Instagram push — we'll post steadily but won't make it the headline";
		case 'autoReplies':
			return 'automated replies — personal feels right for this quarter';
		case 'paidAds':
			return hasBudget
				? "paid ads as the main lever — they're a supporting move, not the headline"
				: 'paid ads — no budget for that yet, so organic is the play';
		case 'findTrends':
			return "chasing trends — we won't force a moment that isn't there";
		case 'emailMarketing':
			return "a heavy email program — we'll start light";
		case 'marketResearch':
			return 'deep research on similar businesses — light tabs are enough for now';
	}
}

export type SummaryParagraph = {
	prefix: string;
	bolds: Array<{ label: string }>;
	connectors: string[];
	suffix: string;
};

export type StrategySummary = {
	opening: string;
	pillars: SlotId[];
	lead: SummaryParagraph | null;
	support: SummaryParagraph | null;
	also: SummaryParagraph | null;
	skip: string;
	close: string;
};

export function buildStrategySummary(
	goal: StrategyGoal | null,
	monthlyBudgetUsd: number,
): StrategySummary {
	const budget = Number.isFinite(monthlyBudgetUsd)
		? Math.max(0, monthlyBudgetUsd)
		: 0;

	const ranked = computeAllRanked(goal, budget);
	const top = ranked.slice(0, 4);
	const pillars: SlotId[] = top.includes('igContent')
		? ['igContent', ...top.filter((s) => s !== 'igContent')]
		: top;
	const deprioritized = ranked.find((s) => !pillars.includes(s));

	const goalPhrase = goal ? GOAL_PHRASE[goal] : 'lining up the strategy';
	const budgetPhrase =
		budget > 0
			? `, with $${budget.toLocaleString()}/month to put behind the wins`
			: ', running organic this quarter';
	const opening = `Got it. We're ${goalPhrase}${budgetPhrase}.`;

	const primary = pillars[0];
	const secondary = pillars[1];
	const supporting = pillars.slice(2);

	const lead: SummaryParagraph | null = primary
		? {
				prefix: 'Lead move: ',
				bolds: [{ label: SLOT_DEFS[primary].label }],
				connectors: [],
				suffix: `. ${capitalize(SLOT_DEFS[primary].lead)}.`,
			}
		: null;

	const support: SummaryParagraph | null = secondary
		? {
				prefix: 'Right behind that: ',
				bolds: [{ label: SLOT_DEFS[secondary].label }],
				connectors: [],
				suffix: `. ${capitalize(SLOT_DEFS[secondary].lead)}.`,
			}
		: null;

	let also: SummaryParagraph | null = null;
	if (supporting.length === 1) {
		const s0 = supporting[0];
		also = {
			prefix: 'Also running: ',
			bolds: [{ label: SLOT_DEFS[s0].label }],
			connectors: [],
			suffix: `. ${capitalize(SLOT_DEFS[s0].short)}.`,
		};
	} else if (supporting.length >= 2) {
		const s0 = supporting[0];
		const s1 = supporting[1];
		also = {
			prefix: 'Also running: ',
			bolds: [{ label: SLOT_DEFS[s0].label }, { label: SLOT_DEFS[s1].label }],
			connectors: [' and '],
			suffix: `. ${capitalize(SLOT_DEFS[s0].short)}. ${capitalize(
				SLOT_DEFS[s1].short,
			)}.`,
		};
	}

	const skip = deprioritized
		? `Skipping for now: ${skipFrameFor(deprioritized, budget)}. Easy to layer in when it's time.`
		: '';

	return {
		opening,
		pillars,
		lead,
		support,
		also,
		skip,
		close: "Let's go.",
	};
}
